/**
 * 系统错误日志 (Admin)
 */
import { authFetch } from './api.js';
import { setSyserrCurrentPage } from './state.js';
import { showToast, renderPagination } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';

const STEP_NAMES = { 1:'ASIN 抓数据',2:'构建关键词库',3:'生成 Listing',4:'合规检测',5:'Listing 打分',6:'修改建议',7:'图片生成' };
const SOURCE_CLASSES = { GEMINI:'syserr-source-gemini',MYSQL:'syserr-source-mysql',SCRAPER:'syserr-source-scraper',QUEUE:'syserr-source-queue',IMAGE:'syserr-source-image',IMAGE_QUEUE:'syserr-source-image',PROCESS:'syserr-source-unknown',UNKNOWN:'syserr-source-unknown' };
const SYSERR_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  source: { type: 'string', defaultValue: '' },
  days: { type: 'string', defaultValue: '' },
};

export function initErrors() {
  hydrateErrorFiltersFromUrl();
  registerViewLoader('admin-system-errors', () => { loadOpsSummary(); loadAdminSystemErrors(); });
  document.getElementById('clear-syserr-btn')?.addEventListener('click', clearSystemErrors);
  document.getElementById('prune-syserr-btn')?.addEventListener('click', pruneOldSystemErrors);
  document.getElementById('syserr-source-filter')?.addEventListener('change', () => loadAdminSystemErrors(1));
  document.getElementById('syserr-days-filter')?.addEventListener('change', () => loadAdminSystemErrors(1));
}

function hydrateErrorFiltersFromUrl() {
  const state = readScopedQuery('syserr', SYSERR_QUERY_SCHEMA);
  const source = document.getElementById('syserr-source-filter');
  const days = document.getElementById('syserr-days-filter');
  if (source) source.value = state.source || '';
  if (days) days.value = state.days || '';
}

function createSystemErrorElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createSystemErrorHeader() {
  const row = document.createElement('tr');
  ['ID', '来源', '路径', '消息', '时间', '操作'].forEach((label) => {
    row.appendChild(createSystemErrorElement('th', '', label));
  });
  return row;
}

function createSystemErrorSummaryCard(label, value, valueClassName = '') {
  const card = createSystemErrorElement('div', 'stat-card');
  card.append(
    createSystemErrorElement('div', 'stat-card-label', label),
    createSystemErrorElement('div', `stat-card-value ${valueClassName}`.trim(), value),
  );
  return card;
}

function createSystemErrorEmptyRow(message) {
  const cell = createSystemErrorElement('td', 'syserr-empty-cell', message);
  cell.colSpan = 6;
  const row = document.createElement('tr');
  row.appendChild(cell);
  return row;
}

function createSystemErrorPath(context = {}) {
  if (context.stepId) {
    const path = createSystemErrorElement('span', 'syserr-path-step', `[${context.stepId}] ${STEP_NAMES[context.stepId] || 'Step'}`);
    if (context.sectionName) {
      path.append(
        document.createTextNode(' '),
        createSystemErrorElement('code', 'syserr-path-section', context.sectionName),
      );
    }
    return path;
  }
  if (context.sectionName) return createSystemErrorElement('code', '', context.sectionName);
  return createSystemErrorElement('span', 'syserr-path-muted', '-');
}

function createSystemErrorRow(item) {
  const row = document.createElement('tr');
  const sourceKey = SOURCE_CLASSES[item.source] ? item.source : 'UNKNOWN';
  const context = item.context_data || {};
  const message = item.error_message
    ? `${item.error_message.slice(0, 80)}${item.error_message.length > 80 ? '…' : ''}`
    : '-';
  row.appendChild(createSystemErrorElement('td', '', item.id));
  row.appendChild(createSystemErrorElement('td', `syserr-source ${SOURCE_CLASSES[sourceKey]}`, item.source || 'UNKNOWN'));

  const pathCell = createSystemErrorElement('td', 'syserr-path-cell');
  pathCell.appendChild(createSystemErrorPath(context));
  row.appendChild(pathCell);
  row.appendChild(createSystemErrorElement('td', 'syserr-message-cell', message));
  row.appendChild(createSystemErrorElement('td', 'syserr-time-cell', new Date(item.created_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })));

  const actionCell = document.createElement('td');
  const button = createSystemErrorElement('button', 'btn btn-secondary btn-sm', '详情');
  button.type = 'button';
  button.dataset.action = 'show-syserr-detail';
  button.dataset.id = String(item.id ?? '');
  actionCell.appendChild(button);
  row.appendChild(actionCell);
  return row;
}

async function loadOpsSummary() {
  try {
    const resp = await authFetch('/api/admin/ops-summary');
    if (!resp.ok) return;
    const data = await resp.json();
    const el = document.getElementById('syserr-stats-grid');
    if (!el || !data?.errors) return;
    const e = data.errors;
    const top = e.bySource?.[0];
    el.replaceChildren(
      createSystemErrorSummaryCard('今日', e.today, 'syserr-summary-value-danger'),
      createSystemErrorSummaryCard('24h', e.last24h),
      createSystemErrorSummaryCard('主要来源', top ? `${top.source} (${top.count})` : '-', 'syserr-summary-value-source'),
    );
  } catch {}
}

export async function loadAdminSystemErrors(page = null) {
  const queryState = readScopedQuery('syserr', SYSERR_QUERY_SCHEMA);
  const targetPage = page == null ? queryState.page : page;
  setSyserrCurrentPage(targetPage);
  const params = new URLSearchParams({ page: targetPage, size: 20 });
  const source = document.getElementById('syserr-source-filter')?.value;
  const days = document.getElementById('syserr-days-filter')?.value;
  writeScopedQuery('syserr', { page: targetPage, source, days });
  if (source) params.set('source', source);
  if (days) params.set('days', days);
  const tbody = document.getElementById('syserr-tbody');
  const data = await withTableLoading({
    tbody,
    colSpan: 6,
    page: targetPage,
    errorMessage: '加载失败',
    onRetry: (p) => loadAdminSystemErrors(p),
    onError: () => showToast('加载系统错误失败', 'error'),
    task: async () => {
    const resp = await authFetch(`/api/admin/system-errors?${params}`);
    if (!resp.ok) throw new Error();
      return resp.json();
    },
  });
  if (!data) return;
    document.getElementById('syserr-thead')?.replaceChildren(createSystemErrorHeader());
    if (!data.rows?.length) {
      tbody.replaceChildren(createSystemErrorEmptyRow('暂无日志'));
      document.getElementById('syserr-pagination')?.replaceChildren();
      return;
    }
    window.__syserrRowsCache = Object.fromEntries(data.rows.map(r => [String(r.id), r]));
    tbody.replaceChildren(...data.rows.map((row) => createSystemErrorRow(row)));
    renderPagination('syserr-pagination', data.total, data.page, data.size, p => loadAdminSystemErrors(p));
}

export async function clearSystemErrors() {
  if (!confirm('清空全部系统错误日志？该操作不可恢复。')) return;
  const token = prompt('请输入 CLEAR 确认清空全部日志：', '');
  if (!token || token.trim().toUpperCase() !== 'CLEAR') {
    showToast('未输入正确确认词，已取消', 'info');
    return;
  }
  try {
    const resp = await authFetch('/api/admin/system-errors/clear?confirm=CLEAR', { method: 'DELETE' });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || '清空失败');
    }
    const data = await resp.json();
    showToast(`已清空 ${data.deleted||0} 条`, 'success');
    loadOpsSummary(); loadAdminSystemErrors(1);
  } catch (err) { showToast(err.message || '清空失败', 'error'); }
}

export async function pruneOldSystemErrors() {
  const days = prompt('删除多少天前的日志？', '30');
  if (!days || !/^\d+$/.test(days)) return;
  if (!confirm(`删除 ${days} 天前的日志？`)) return;
  try {
    const resp = await authFetch(`/api/admin/system-errors/clear?days=${days}`, { method: 'DELETE' });
    const data = await resp.json();
    showToast(`已删除 ${data.deleted||0} 条`, 'success');
    loadOpsSummary(); loadAdminSystemErrors(1);
  } catch { showToast('清理失败', 'error'); }
}

export function showSysErrDetail(id) {
  const row = window.__syserrRowsCache?.[String(id)];
  if (!row) { showToast('请刷新后重试', 'error'); return; }
  const cd = row.context_data || {};
  let detail = `来源: ${row.source}\n`;
  if (row.error_message) detail += `\n━━━ 消息 ━━━\n${row.error_message}`;
  if (cd.stepId || cd.sectionName) {
    detail += '\n\n━━━ 路径 ━━━\n';
    if (cd.stepId) detail += `步骤: [${cd.stepId}] ${STEP_NAMES[cd.stepId]||''}\n`;
    if (cd.sectionName) detail += `模块: ${cd.sectionName}\n`;
    if (cd.asin) detail += `ASIN: ${cd.asin}\n`;
    if (cd.jobId) detail += `Job: ${cd.jobId}\n`;
  }
  const rest = { ...cd }; ['stepId','sectionName','phase','asin','jobId'].forEach(k=>delete rest[k]);
  if (Object.keys(rest).length) detail += '\n\n━━━ 上下文 ━━━\n'+JSON.stringify(rest,null,2);
  if (row.stack_trace) detail += '\n\n━━━ 堆栈 ━━━\n'+row.stack_trace;
  document.getElementById('detail-modal-title').textContent = `错误 #${row.id} — ${row.source}`;
  document.getElementById('detail-modal-content').textContent = detail;
  document.getElementById('detail-modal').hidden = false;
}
